import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import axios from "axios";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiEndpoint = `${API_BASE_URL}/popage/indent`;

const POIndent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { role } = useSelector((state) => state.auth);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });

  const navigate = useNavigate();

  // Single common API endpoint
  const getApiEndpoint = () => `${API_BASE_URL}/adminpage/poindent`;
  const getEditFields = () => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options:
        dropdownData.projects.map((project) => ({
          value: project.id,
          label: project.name,
        })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options:
        dropdownData.types.map((type) => ({
          value: type.id,
          label: type.name,
        })) || [],
    },
  ];

  const fetchIndents = async () => {
    try {
      console.log("Fetching indents - START");
      console.log("Current user role:", role);

      const apiEndpoint = getApiEndpoint();
      console.log("Using API endpoint:", apiEndpoint);

      const response = await axios.get(apiEndpoint);
      console.log("Raw API Response:", JSON.parse(JSON.stringify(response.data)));

      const formattedIndents = response.data.map((indent) => ({
        ...indent,
        L1: indent.L1 ? "Approved" : "Pending",
        L2: indent.L2 ? "Approved" : "Pending",
        PO: indent.PO ? "Approved" : "Pending",
        // Keep the dates as they come from the server since they're already formatted
        CreationDate: indent.CreationDate || "N/A",
        PEApprovalDate: indent.PEApprovalDate || "N/A",
        L1ApprovedDate: indent.L1ApprovedDate || "N/A",
        L2ApprovalDate: indent.L2ApprovalDate || "N/A"
      }));

      console.log("Formatted Indents:", formattedIndents);
      console.log("Formatted Indents Length:", formattedIndents.length);

      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
    } catch (error) {
      console.error("Detailed Error Fetching Indents:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        role: role,
        endpoint: getApiEndpoint(),
      });
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };

      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setFilteredIndents(indents);
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
      const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.Status,
        indent.ProjectName,
        indent.TypeName,
        indent.CreationDate?.toString(),
        indent.TaskSchedule,
        indent.L1,
        indent.L2,
        indent.PO
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
        );
      });
      setFilteredIndents(filtered);
  };

  const handleDateFilter = (filterOptions) => {
    const { dateFrom, dateTo, indentType } = filterOptions;
    let filtered = indents;

    // Apply date filter if dates are provided
    if (dateFrom && dateTo) {
      filtered = filtered.filter((indent) => {
        // Parse the date in dd/mm/yy format
        const [day, month, year] = indent.CreationDate.split("/");
        const creationDate = new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
    }

    // Apply indent type filter if selected
    if (indentType) {
      filtered = filtered.filter((indent) => {
        // Check both TypeName and Type fields
        const typeName = indent.TypeName || indent.Type || "";
        return typeName.toLowerCase() === indentType.toLowerCase();
      });
    }

    setFilteredIndents(filtered);
  };

  const handleRedirect = (page) => {
    navigate(page);
  };

  const handleRowClick = (row) => {
    navigate(`/POtable`, { state: { row } });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">PD Table</h3>
        </div>

        {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields()}
            apiEndpoint={apiEndpoint}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={fetchIndents}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole={role}
            addLink="/addl2Indent"
            editRoute="/POTable"
            searchQuery={searchQuery}
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={[
              { key: "IndentID", label: "Indent ID" },
              { key: "CreationDate", label: "Creation Date" },
              { key: "ProjectName", label: "Project" },
              { key: "TypeName", label: "Indent Type" },
              { key: "TaskSchedule", label: "Task Scheduled" },
              { key: "Status", label: "Status" },
              { key: "ItemCount", label: "Item Count" },
            ]}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={(row) => setSelectedRow(row)}
            onDoubleClick={(row) => handleRowClick(row)}
            rowKey="IndentID"
            userIdKey="UserID"
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole="admin"
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default POIndent;
